import React from 'react'

import {
    useGetIndividualChallengeEvaluationReportQuery,
} from './evaluationReportsApiSlice'

const EtIndividualEvaluationReportPreview = ({
    orgId,
    trailId,
    sessionId,
    challengeId,
    userId,
    evaluationNumber,
}) => {

    // First Created - 04 Aug 2026
    // Author - Atul Pandey
    // Purpose - Preview normalized individual evaluation report data


    const shouldSkip =
        !orgId ||
        !trailId ||
        !sessionId ||
        !challengeId ||
        !userId ||
        !evaluationNumber

    const {
        data: report,
        isLoading,
        isFetching,
        isSuccess,
        isError,
        error,
        refetch,
    } =
        useGetIndividualChallengeEvaluationReportQuery(
            {
                orgId,
                trailId,
                sessionId,
                challengeId,
                userId,
                evaluationNumber,
            },
            {
                skip: shouldSkip,
            }
        )

    if (shouldSkip) {
        return (
            <div className="rounded-xl border border-slate-200 bg-white p-5">
                <p className="text-sm text-slate-500">
                    Report parameters are incomplete.
                </p>
            </div>
        )
    }

    if (isLoading) {
        return (
            <div className="rounded-xl border border-slate-200 bg-white p-5">
                <p className="text-sm text-slate-500">
                    Loading evaluation report...
                </p>
            </div>
        )
    }

    if (isError) {
        return (
            <div className="rounded-xl border border-red-200 bg-red-50 p-5">
                <p className="font-medium text-red-700">
                    The evaluation report could not be loaded.
                </p>

                <p className="mt-2 text-sm text-red-600">
                    {error?.data?.message ||
                        'An unexpected reporting error occurred.'}
                </p>

                <button
                    type="button"
                    onClick={refetch}
                    className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white"
                >
                    Try Again
                </button>
            </div>
        )
    }

    if (!isSuccess || !report) {
        return null
    }

    const {
        reportContext,
        evaluation,
        summary,
        warnings,
    } = report

    return (
        <div className="space-y-6 rounded-2xl border border-slate-200 bg-white p-6">
            {/* Report Header */}
            <div className="border-b border-slate-200 pb-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                            Evaluation Report Preview
                        </p>

                        <h2 className="mt-1 text-2xl font-semibold text-slate-900">
                            {
                                reportContext
                                    ?.challenge
                                    ?.challengeName
                            }
                        </h2>

                        <p className="mt-1 text-sm text-slate-600">
                            {
                                reportContext
                                    ?.subjectUser
                                    ?.displayName
                            }
                            {' • '}
                            {
                                reportContext
                                    ?.session
                                    ?.sessionName
                            }
                            {' • '}
                            {
                                reportContext
                                    ?.trail
                                    ?.trailName
                            }
                        </p>
                    </div>

                    <div className="rounded-full bg-orange-50 px-4 py-2 text-sm font-medium text-orange-700">
                        {
                            evaluation
                                ?.evaluationLabel
                        }
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <SummaryCard
                    label="Self Average"
                    value={
                        summary
                            ?.self
                            ?.averageScore
                    }
                />

                <SummaryCard
                    label="Coach Average"
                    value={
                        summary
                            ?.coaches
                            ?.combinedAverageScore
                    }
                />

                <SummaryCard
                    label="Sponsor Average"
                    value={
                        summary
                            ?.sponsors
                            ?.combinedAverageScore
                    }
                />

                <SummaryCard
                    label="Evaluators"
                    value={
                        summary
                            ?.availableEvaluatorCount
                    }
                />
            </div>

            {/* Evaluators */}
            <div>
                <h3 className="text-lg font-semibold text-slate-900">
                    Evaluators
                </h3>

                <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                    {evaluation
                        ?.evaluators
                        ?.self
                        ?.available && (
                        <EvaluatorCard
                            role="Self"
                            evaluator={
                                evaluation
                                    .evaluators
                                    .self
                            }
                        />
                    )}

                    {evaluation
                        ?.evaluators
                        ?.coaches
                        ?.map(
                            (coach) => (
                                <EvaluatorCard
                                    key={
                                        coach
                                            .evaluationId
                                    }
                                    role="Coach"
                                    evaluator={
                                        coach
                                    }
                                />
                            )
                        )}

                    {evaluation
                        ?.evaluators
                        ?.sponsors
                        ?.map(
                            (sponsor) => (
                                <EvaluatorCard
                                    key={
                                        sponsor
                                            .evaluationId
                                    }
                                    role="Sponsor"
                                    evaluator={
                                        sponsor
                                    }
                                />
                            )
                        )}
                </div>
            </div>

            {/* Dimension Validation Table */}
            <div>
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <h3 className="text-lg font-semibold text-slate-900">
                        Dimension Scores
                    </h3>

                    {isFetching && (
                        <span className="text-xs text-slate-500">
                            Refreshing...
                        </span>
                    )}
                </div>

                <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200">
                    <table className="min-w-full divide-y divide-slate-200 text-sm">
                        <thead className="bg-slate-50">
                            <tr>
                                <th className="px-4 py-3 text-left font-medium text-slate-600">
                                    Dimension
                                </th>

                                <th className="px-4 py-3 text-center font-medium text-slate-600">
                                    Self
                                </th>

                                {evaluation
                                    ?.evaluators
                                    ?.coaches
                                    ?.map(
                                        (
                                            coach
                                        ) => (
                                            <th
                                                key={
                                                    coach
                                                        .evaluationId
                                                }
                                                className="px-4 py-3 text-center font-medium text-slate-600"
                                            >
                                                {
                                                    coach
                                                        ?.evaluator
                                                        ?.displayName
                                                }
                                            </th>
                                        )
                                    )}

                                {evaluation
                                    ?.evaluators
                                    ?.sponsors
                                    ?.map(
                                        (
                                            sponsor
                                        ) => (
                                            <th
                                                key={
                                                    sponsor
                                                        .evaluationId
                                                }
                                                className="px-4 py-3 text-center font-medium text-slate-600"
                                            >
                                                {
                                                    sponsor
                                                        ?.evaluator
                                                        ?.displayName
                                                }
                                            </th>
                                        )
                                    )}
                            </tr>
                        </thead>

                        <tbody className="divide-y divide-slate-100 bg-white">
                            {evaluation
                                ?.dimensions
                                ?.map(
                                    (
                                        dimension
                                    ) => (
                                        <tr
                                            key={
                                                dimension
                                                    .dimensionId
                                            }
                                        >
                                            <td className="px-4 py-3">
                                                <p className="font-medium text-slate-800">
                                                    {
                                                        dimension
                                                            .dimensionName
                                                    }
                                                </p>

                                                <p className="mt-1 text-xs text-slate-500">
                                                    {
                                                        dimension
                                                            .dimensionDescription
                                                    }
                                                </p>
                                            </td>

                                            <ScoreCell
                                                score={
                                                    dimension
                                                        ?.self
                                                        ?.score
                                                }
                                            />

                                            {dimension
                                                ?.coaches
                                                ?.map(
                                                    (
                                                        coach
                                                    ) => (
                                                        <ScoreCell
                                                            key={
                                                                coach
                                                                    .evaluationId
                                                            }
                                                            score={
                                                                coach
                                                                    .score
                                                            }
                                                        />
                                                    )
                                                )}

                                            {dimension
                                                ?.sponsors
                                                ?.map(
                                                    (
                                                        sponsor
                                                    ) => (
                                                        <ScoreCell
                                                            key={
                                                                sponsor
                                                                    .evaluationId
                                                            }
                                                            score={
                                                                sponsor
                                                                    .score
                                                            }
                                                        />
                                                    )
                                                )}
                                        </tr>
                                    )
                                )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Validation Warnings */}
            {(warnings?.multipleSelfEvaluations ||
                warnings?.mixedEvaluationTypes ||
                warnings?.noChallengeDimensions) && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                    <h3 className="font-medium text-amber-800">
                        Report Validation Warnings
                    </h3>

                    <pre className="mt-2 overflow-x-auto text-xs text-amber-700">
                        {JSON.stringify(
                            warnings,
                            null,
                            2
                        )}
                    </pre>
                </div>
            )}
        </div>
    )
}

const SummaryCard = ({
    label,
    value,
}) => (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
            {label}
        </p>

        <p className="mt-2 text-2xl font-semibold text-slate-900">
            {value ?? 'N/A'}
        </p>
    </div>
)

const EvaluatorCard = ({
    role,
    evaluator,
}) => (
    <div className="rounded-xl border border-slate-200 p-4">
        <div className="flex items-center justify-between gap-3">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                {role}
            </span>

            <span
                className={
                    evaluator?.completed
                        ? 'rounded-full bg-green-50 px-2 py-1 text-xs font-medium text-green-700'
                        : 'rounded-full bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700'
                }
            >
                {evaluator?.completed
                    ? 'Submitted'
                    : 'Pending'}
            </span>
        </div>

        <p className="mt-3 font-medium text-slate-900">
            {evaluator
                ?.evaluator
                ?.displayName ||
                'Not available'}
        </p>

        <p className="mt-1 text-xs text-slate-500">
            {evaluator?.evaluationType ||
                'Evaluation type unavailable'}
        </p>
    </div>
)

const ScoreCell = ({
    score,
}) => (
    <td className="px-4 py-3 text-center">
        <span className="inline-flex min-w-10 justify-center rounded-lg bg-slate-100 px-2 py-1 font-medium text-slate-800">
            {typeof score === 'number'
                ? score
                : '—'}
        </span>
    </td>
)

export default EtIndividualEvaluationReportPreview